import rulesJson from '@shared/schemas/rules.json';
import type { PersonalityId, SkillId } from '@shared/protocol/messages';

export interface SkillDef {
  id: SkillId;
  name: string;
  cost: number;
  description: string;
}

export interface PersonalityDef {
  id: PersonalityId;
  name: string;
  description: string;
}

export interface TierDef {
  id: 'doodle' | 'sketch' | 'masterpiece';
  name: string;
  budget: number;
}

export interface StatRange {
  min: number;
  max: number;
}

export interface UltimateDef {
  /** Damage to the foe, % of the foe's max HP. */
  damagePercent?: number;
  /** Heal, % of your own max HP. */
  healPercent?: number;
  /** Heal by the damage dealt. */
  drain?: boolean;
  /** Take no rock-paper-scissors damage this round. */
  shield?: boolean;
  /** Extra damage % while below half HP. */
  rageBonusPercent?: number;
}

export interface GameRules {
  version: number;
  damage: {
    atkScale: number;
    defScale: number;
    minDamage: number;
    comboStepPercent: number;
    comboCapPercent: number;
    critBonusPercent: number;
    critChanceCap: number;
  };
  battle: {
    maxRounds: number;
    pvpTurnTimeoutMs: number;
    pvpMaxConsecutiveTimeouts: number;
    reconnectGraceMs: number;
  };
  stats: { hp: StatRange; atk: StatRange; def: StatRange; luck: StatRange };
  budget: {
    costHp: number;
    costAtk: number;
    costDef: number;
    costLuck: number;
    tiers: TierDef[];
    pvpPublicBudget: number;
    rewardExpPerPower: number;
    rewardCoinsPerPower: number;
  };
  skills: SkillDef[];
  skillParams: {
    crayonSmashBonusPercent: number;
    paperShieldReducePercent: number;
    eraserMendHealPercent: number;
    doodleDodgeChance: number;
    inkComboMultiplier: number;
    luckyStarCritBonusPercent: number;
    highlighterDrainPercent: number;
    sharpenerSpikesPercent: number;
    markerRageBonusPercent: number;
  };
  /** Guard / Focus stances (see engine.ts). */
  stances: {
    /** Guard: damage taken on a lost round and dealt on a won round are cut by this %. */
    guardPercent: number;
    /** Focus: winning adds this % to the damage percent sum. */
    focusBonusPercent: number;
    /** Focus: losing increases the damage taken by this %. */
    focusVulnerablePercent: number;
    /** Rounds after a stance during which that side cannot use any stance. */
    stanceCooldownRounds: number;
  };
  /** Ink gauge that powers the class ultimate. */
  ink: {
    max: number;
    /** Ink for winning a round. */
    winGain: number;
    /** Ink for losing a round and taking damage. */
    hurtGain: number;
    /** Extra ink for a combo chain burst. */
    burstGain: number;
  };
  /** Combo chain: win `length` rounds in a row with all-different moves for a bonus hit. */
  chain: {
    length: number;
    /** Burst damage as a % of the foe's max HP. */
    burstPercent: number;
  };
  /** Ultimate per skill ('none' = no skill). Percents are of max HP. */
  ultimates: Record<SkillId, UltimateDef>;
  personalities: PersonalityDef[];
  progression: {
    baseExpToLevel: number;
    expGrowthPercent: number;
    maxLevel: number;
    statPointsPerLevel: number;
    hpPerPoint: number;
    lossExpPercent: number;
  };
}

/** Balance data shared with the Go server (shared/schemas/rules.json). */
export const RULES = rulesJson as GameRules;

const skillMap = new Map<SkillId, SkillDef>(RULES.skills.map((s) => [s.id, s]));

export function skillDef(id: SkillId): SkillDef {
  const def = skillMap.get(id);
  if (!def) throw new Error(`unknown skill ${id}`);
  return def;
}

export function isSkillId(v: unknown): v is SkillId {
  return typeof v === 'string' && skillMap.has(v as SkillId);
}

export function isPersonalityId(v: unknown): v is PersonalityId {
  return typeof v === 'string' && RULES.personalities.some((p) => p.id === v);
}

export function tierDef(id: TierDef['id']): TierDef {
  const t = RULES.budget.tiers.find((x) => x.id === id);
  if (!t) throw new Error(`unknown tier ${id}`);
  return t;
}
